import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { buildTerminalTheme } from "@fwf/fwfa-design-system/react";
import type { DebugLogEntry } from "@/types";
import { DebugLogContent } from "@/components/DebugPanel";
import "@xterm/xterm/css/xterm.css";

type Tab = "terminal" | "debug";

const DEFAULT_PANEL_HEIGHT = 224;
const MIN_PANEL_HEIGHT = 160;
const AUTH_MIN_PANEL_HEIGHT = 288; // ~12 terminal lines
const AUTH_TARGET_RATIO = 0.5; // 50% of viewport when auth active
const MAX_PANEL_HEIGHT_RATIO = 0.7;
const PANEL_HEIGHT_STORAGE_KEY = "freshvibe:bottom-panel-height";

function clampPanelHeight(height: number): number {
  if (typeof window === "undefined") {
    return Math.max(Math.round(height), MIN_PANEL_HEIGHT);
  }
  const maxHeight = Math.max(
    MIN_PANEL_HEIGHT,
    Math.floor(window.innerHeight * MAX_PANEL_HEIGHT_RATIO),
  );
  return Math.min(Math.max(Math.round(height), MIN_PANEL_HEIGHT), maxHeight);
}

function loadPanelHeight(): number {
  if (typeof window === "undefined") return DEFAULT_PANEL_HEIGHT;
  const raw = window.localStorage.getItem(PANEL_HEIGHT_STORAGE_KEY);
  const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
  if (!Number.isFinite(parsed)) return DEFAULT_PANEL_HEIGHT;
  return clampPanelHeight(parsed);
}

interface Props {
  visible: boolean;
  debugEntries: DebugLogEntry[];
  onClearDebug: () => void;
  onClose: () => void;
  /** Which tab to activate when opening */
  activeTab?: Tab;
  onTabChange?: (tab: Tab) => void;
  /** When true, auto-expand to ~50% viewport (min 12 terminal lines) */
  authActive?: boolean;
}

// ─── Debug Log Tab (shared from DebugPanel.tsx) ───

// ─── Terminal Tab ───

interface TermInstance {
  term: import("@xterm/xterm").Terminal;
  fitAddon: import("@xterm/addon-fit").FitAddon;
}

function ShellTerminal({ visible }: { visible: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const instRef = useRef<TermInstance | null>(null);
  // Set when the shell exits so the next time the user activates the terminal
  // tab we respawn a fresh shell exactly once — recovery without a page reload.
  // Guarding respawn on a user-driven visibility activation (rather than firing
  // it from onPtyExit) means a shell that dies instantly just shows its exit
  // banner instead of thrashing in a respawn loop.
  const shellExitedRef = useRef(false);
  // Mirrors `visible` so the exit callback (wired once, on mount) can tell
  // whether the terminal tab is on screen right now.
  const visibleRef = useRef(visible);
  // Caps automatic respawns at one per mount. If a shell that we auto-respawned
  // dies again, we stop auto-respawning and leave the banner, so a crash-looping
  // shell cannot spin forever — the user recovers by reopening the tab.
  const autoRespawnedRef = useRef(false);

  // Respawn a fresh shell into the existing xterm. Shared by the on-activation
  // recovery and the visible-tab auto-recovery so both start from a clean slate.
  const respawnShell = useCallback(() => {
    shellExitedRef.current = false;
    const inst = instRef.current;
    if (!inst) return;
    inst.term.write("\r\n\x1b[90m[starting a new shell…]\x1b[0m\r\n");
    window.freshvibe.ptySpawn(inst.term.cols, inst.term.rows);
  }, []);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const fit = useCallback(() => {
    const inst = instRef.current;
    const container = containerRef.current;
    if (!inst || !container) return;
    if (container.clientWidth === 0 || container.clientHeight === 0) return;
    inst.fitAddon.fit();
    window.freshvibe.ptyResize(inst.term.cols, inst.term.rows);
  }, []);

  // One-time setup: create xterm, wire IPC, spawn PTY
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    const container = containerRef.current;

    Promise.all([
      import("@xterm/xterm"),
      import("@xterm/addon-fit"),
    ]).then(([{ Terminal }, { FitAddon }]) => {
      if (cancelled) return;

      const term = new Terminal({
        cursorBlink: true,
        fontSize: 12,
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        theme: buildTerminalTheme(),
      });

      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      term.open(container);
      instRef.current = { term, fitAddon };
      window.requestAnimationFrame(() => fit());

      // xterm input → PTY
      term.onData((data: string) => window.freshvibe.ptyInput(data));

      // PTY output → xterm
      const unsubOutput = window.freshvibe.onPtyOutput((data: string) => term.write(data));

      // PTY exited
      const unsubExit = window.freshvibe.onPtyExit((code: number | null) => {
        shellExitedRef.current = true;
        // If the terminal tab is on screen, the once-per-activation recovery can
        // never fire (visibility doesn't change), so the user would be stranded
        // typing into a dead shell. Auto-respawn once instead. The cap keeps a
        // crash-looping shell from thrashing: after one auto-respawn we fall back
        // to the banner and wait for the user to reopen the tab.
        if (visibleRef.current && !autoRespawnedRef.current) {
          autoRespawnedRef.current = true;
          respawnShell();
          return;
        }
        term.write(
          `\r\n\x1b[90m[shell exited with code ${code}] — reopen this tab to start a new shell\x1b[0m\r\n`,
        );
      });

      unsubs = [unsubOutput, unsubExit];

      // Spawn shell with the current xterm dimensions
      window.freshvibe.ptySpawn(term.cols, term.rows);
    });

    let unsubs: ((() => void) | void)[] = [];

    return () => {
      cancelled = true;
      for (const u of unsubs) u?.();
      instRef.current?.term.dispose();
      instRef.current = null;
      window.freshvibe.ptyKill();
    };
  }, [fit]);

  // Fit terminal & sync PTY size when visibility or window size changes.
  // If the shell had exited, activating the tab respawns a fresh one once.
  useEffect(() => {
    if (visible) {
      if (shellExitedRef.current) {
        respawnShell();
      }
      // Reopening the tab is a fresh, user-driven recovery, so re-arm the
      // one-shot visible-tab auto-respawn for the newly spawned shell.
      autoRespawnedRef.current = false;
      const id = setTimeout(fit, 50);
      return () => clearTimeout(id);
    }
  }, [visible, fit, respawnShell]);

  useEffect(() => {
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [fit]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => fit());
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [fit]);

  return (
    <div
      ref={containerRef}
      data-private="workspace-terminal"
      className="ph-no-capture flex-1 min-h-0"
      style={{ display: visible ? "block" : "none" }}
    />
  );
}

// ─── Bottom Panel (container) ───

export function BottomPanel({
  visible,
  debugEntries,
  onClearDebug,
  onClose,
  activeTab: controlledTab,
  onTabChange,
  authActive,
}: Props) {
  const [internalTab, setInternalTab] = useState<Tab>("terminal");
  const [panelHeight, setPanelHeight] = useState(loadPanelHeight);
  const [isResizing, setIsResizing] = useState(false);
  const dragStateRef = useRef<{ startY: number; startHeight: number } | null>(
    null,
  );
  const tab = controlledTab ?? internalTab;
  const setTab = onTabChange ?? setInternalTab;

  // Track whether the shell terminal has been mounted at least once
  const [shellMounted, setShellMounted] = useState(false);

  useEffect(() => {
    if (visible && tab === "terminal" && !shellMounted) {
      setShellMounted(true);
    }
  }, [visible, tab, shellMounted]);

  // Auto-expand panel when auth flow is active
  useEffect(() => {
    if (!authActive || !visible) return;
    const authTarget = clampPanelHeight(
      Math.max(window.innerHeight * AUTH_TARGET_RATIO, AUTH_MIN_PANEL_HEIGHT),
    );
    if (panelHeight < authTarget) {
      setPanelHeight(authTarget);
    }
  }, [authActive, visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      setPanelHeight((prev) => clampPanelHeight(prev));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(
      PANEL_HEIGHT_STORAGE_KEY,
      String(panelHeight),
    );
  }, [panelHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const deltaY = dragState.startY - event.clientY;
      setPanelHeight(clampPanelHeight(dragState.startHeight + deltaY));
    };

    const stopResizing = () => {
      dragStateRef.current = null;
      setIsResizing(false);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopResizing);
    window.addEventListener("pointercancel", stopResizing);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResizing);
      window.removeEventListener("pointercancel", stopResizing);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  const handleResizeStart = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!visible) return;
      dragStateRef.current = {
        startY: event.clientY,
        startHeight: panelHeight,
      };
      setIsResizing(true);
      event.preventDefault();
    },
    [panelHeight, visible],
  );

  return (
    <div
      aria-hidden={!visible}
      className={`flex flex-col overflow-hidden ${
        isResizing ? "" : "transition-[height,opacity] duration-150 ease-out"
      } ${
        visible
          ? "border-t border-line-faint bg-navy-dark opacity-100"
          : "border-t-0 border-transparent bg-transparent opacity-0 pointer-events-none"
      }`}
      style={{ height: visible ? panelHeight : 0 }}
    >
      <div
        onPointerDown={handleResizeStart}
        className="flex h-3 flex-shrink-0 cursor-row-resize items-center justify-center"
      >
        <div className="h-1 w-12 rounded-full bg-surface-strong transition-colors hover:bg-fwf-yellow/70" />
      </div>

      {/* Tab bar */}
      <div className="flex items-center justify-between px-2 border-b border-line-faint flex-shrink-0">
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => setTab("terminal")}
            className={`px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-wider border-b-2 transition-colors ${
              tab === "terminal"
                ? "border-gold text-gold"
                : "border-transparent text-stone-500 hover:text-stone-300"
            }`}
          >
            Terminal
          </button>
          <button
            onClick={() => setTab("debug")}
            className={`px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-wider border-b-2 transition-colors ${
              tab === "debug"
                ? "border-gold text-gold"
                : "border-transparent text-stone-500 hover:text-stone-300"
            }`}
          >
            Debug Log
            {debugEntries.length > 0 && (
              <span className="ml-1.5 text-stone-600">
                ({debugEntries.length})
              </span>
            )}
          </button>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded text-stone-500 hover:text-stone-300 hover:bg-white/[0.07] transition-colors"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Tab content */}
      {shellMounted && (
        <ShellTerminal visible={visible && tab === "terminal"} />
      )}
      {tab === "debug" && (
        <div data-private="workspace-debug-log" className="ph-no-capture flex min-h-0 flex-1 flex-col">
          <DebugLogContent entries={debugEntries} onClear={onClearDebug} />
        </div>
      )}
    </div>
  );
}
